import React, { useEffect } from 'react';
import { useAppSelector, useAppDispatch } from 'flow/hooks/useRedux';

import { addUserPhoneLogEntry } from 'flow/utils/browser-utils';
import {
    getPendingTransferNumber,
    getPendingTransferStartTime,
} from 'flow/selectors/call-selectors';
import { getContactDataForNumber } from 'flow/selectors/contact-selectors';
import { lookupNumber } from 'flow/actions/contact-actions';
import {
    cancelAttendedTransfer,
    completeAttendedTransfer,
} from 'flow/actions/phone-actions';
import { Contact } from 'flow/types/Contact';
import { TypedMap } from 'flow/types/TypedMap';
import SmallCallItem from './SmallCallItem';
import LargeCallItem from './LargeCallItem';

interface Props {
    itemSize: 'small' | 'large';
    displayMode: string;
    direction: string;
    contact: TypedMap<Contact>;
    remoteNumber: string;
    ownCountryCode: string;
}

const PendingAttendedTransferCallItems = ({
    itemSize,
    displayMode,
    direction,
    contact,
    remoteNumber,
    ownCountryCode,
}: Props) => {
    const dispatch = useAppDispatch();

    const transferNumber = useAppSelector(getPendingTransferNumber);

    const transferContact = useAppSelector((state) =>
        //@ts-ignore
        getContactDataForNumber(state, transferNumber),
    );

    const transferStartTime = useAppSelector(getPendingTransferStartTime);

    useEffect(() => {
        if (transferNumber && !transferContact) {
            addUserPhoneLogEntry(`Doing numberlookup on ${transferNumber}`);
            dispatch(lookupNumber(transferNumber));
        }
    }, [transferNumber, transferContact]);

    useEffect(() => {
        addUserPhoneLogEntry(
            `Attended transfer call items mounted, held: ${remoteNumber} active: ${transferNumber}`,
        );
        return () => {
            addUserPhoneLogEntry('Attended transfer call items unmounted');
        };
    }, [remoteNumber, transferNumber]);

    const transferTargetProps = {
        direction: 'outgoing',
        remoteNumber: transferNumber,
        contact: transferContact,
        startTime: transferStartTime,
        onTransferCancel: () => dispatch(cancelAttendedTransfer()),
        onTransferComplete: () => dispatch(completeAttendedTransfer()),
        displayMode,
        ownCountryCode,
        isEnded: false,
        isAccepted: true,
        isOnHold: false,
    };

    return (
        <>
            <SmallCallItem
                isAwaitingTransfer={true}
                direction={direction}
                contact={contact}
                remoteNumber={remoteNumber}
                isOnHold={true}
                ownCountryCode={ownCountryCode}
                isEnded={false}
                isAccepted={true}
            />
            {itemSize === 'large' ? (
                <LargeCallItem {...transferTargetProps} />
            ) : (
                <SmallCallItem {...transferTargetProps} />
            )}
        </>
    );
};

export default PendingAttendedTransferCallItems;
